import { AddressSuggestion } from '@/modules/Client/Interfaces/AddressSuggestion';

// Numeric European postal codes, including Polish codes such as 00-793.
const postalCodePattern = '\\d{2}-\\d{3}|\\d{3}[- ]\\d{2}|\\d{4}-\\d{3}|\\d{4,6}';
const postalCodeBeforeCity = new RegExp(`^(${postalCodePattern})\\s+(.+)$`);
const postalCodeAfterCity = new RegExp(`^(.+?)\\s+(${postalCodePattern})$`);

function parsePostalCodeAndCity(location: string) {
    const postalCodeFirst = location.match(postalCodeBeforeCity);
    if (postalCodeFirst) {
        const [, poNumber, city] = postalCodeFirst;
        return { poNumber, city };
    }

    const postalCodeLast = location.match(postalCodeAfterCity);
    if (postalCodeLast) {
        const [, city, poNumber] = postalCodeLast;
        return { poNumber, city };
    }

    return null;
}

function parseFormattedAddress(formattedAddress: string) {
    // Preserve empty parts: a missing field must not shift the following fields.
    const parts = formattedAddress.split(',').map((part) => part.trim());

    // Expected format: street lines, postal code and city, country.
    // The trailing country is ignored; it is never applied to the form.
    const lastLocationIndex = parts.length - 2;
    const firstLocationIndex = parts.length > 2 ? 1 : 0;

    for (let index = lastLocationIndex; index >= firstLocationIndex; index--) {
        const location = parsePostalCodeAndCity(parts[index]);
        if (location) {
            return {
                streetNameAndNumber: parts.slice(0, index).join(', '),
                poNumber: location.poNumber,
                city: location.city,
            };
        }
    }

    // Without a recognized postcode, use the city immediately before the country.
    if (parts.length >= 3) {
        return {
            streetNameAndNumber: parts.slice(0, lastLocationIndex).join(', '),
            poNumber: '',
            city: parts[lastLocationIndex],
        };
    }

    return { streetNameAndNumber: parts[0], poNumber: '', city: '' };
}

export function parseAddressSuggestion(suggestion: AddressSuggestion) {
    const parsedAddress = parseFormattedAddress(suggestion.formattedAddress);
    const postalAddress = suggestion.postalAddress;

    return {
        streetNameAndNumber: postalAddress?.addressLines?.join(', ') ?? parsedAddress.streetNameAndNumber,
        poNumber: postalAddress?.postalCode ?? parsedAddress.poNumber,
        city: postalAddress?.locality ?? parsedAddress.city,
    };
}
